import React, { useState, useEffect } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { LysepAPI } from '../../../contexts/ApiContext';

const AddUserModal = ({ isOpen, onClose, roles, onSuccess }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        role: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Default role selection
            const defaultRole = roles.length > 0
                ? (roles[0]?.name || roles[0]?.role_name || roles[0])
                : 'user';

            setFormData({
                username: '',
                email: '',
                password: '',
                role: defaultRole
            });
            setErrors({});
        }
    }, [isOpen, roles]);

    if (!isOpen) return null;

    const validateEmail = (email) => {
        if (!email) return true; // Optional
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.username || formData.username.length < 3) {
            newErrors.username = 'Kullanıcı adı en az 3 karakter olmalıdır';
        }

        if (formData.email && !validateEmail(formData.email)) {
            newErrors.email = 'Geçerli bir e-posta adresi giriniz';
        }

        if (!formData.password || formData.password.length < 6) {
            newErrors.password = 'Şifre en az 6 karakter olmalıdır';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error('Lütfen form hatalarını düzeltiniz');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                username: formData.username.trim(),
                password: formData.password,
                role: formData.role
            };

            if (formData.email && formData.email.trim()) {
                payload.email = formData.email.trim();
            }

            console.log('Creating user with payload:', { ...payload, password: '***' });

            const response = await LysepAPI.post('/api/user', payload);

            if (response.data && response.data.state === true) {
                toast.success(response.data.message || 'Kullanıcı başarıyla eklendi!');
                onSuccess?.();
                onClose();
            } else {
                const errorMsg = response.data?.message || 'Kullanıcı eklenirken hata oluştu';
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('User creation error:', error);

            let errorMessage = 'Bağlantı hatası oluştu';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getRoleValue = (role) => {
        if (typeof role === 'string') return role;
        return role?.name || role?.role_name || role?.toString() || role;
    };

    const getRoleLabel = (role) => {
        if (typeof role === 'string') return role;
        return role?.name || role?.role_name || role?.toString() || 'Unknown';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn">
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-[#252525]">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-orange-500" />
                        Yeni Kullanıcı Ekle
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                        disabled={isSubmitting}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400">Kullanıcı Adı <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            className={`w-full bg-[#0a0e13] border ${errors.username ? 'border-red-500' : 'border-white/10'} rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors`}
                            placeholder="Kullanıcı adı"
                            disabled={isSubmitting}
                        />
                        {errors.username && <p className="text-xs text-red-500">{errors.username}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400">E-posta</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className={`w-full bg-[#0a0e13] border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors`}
                            placeholder="ornek@email.com (Opsiyonel)"
                            disabled={isSubmitting}
                        />
                        {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400">Şifre <span className="text-red-500">*</span></label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className={`w-full bg-[#0a0e13] border ${errors.password ? 'border-red-500' : 'border-white/10'} rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors`}
                            placeholder="******"
                            disabled={isSubmitting}
                        />
                        {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400">Rol <span className="text-red-500">*</span></label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                            disabled={isSubmitting}
                        >
                            {roles.map((role, index) => (
                                <option key={index} value={getRoleValue(role)}>
                                    {getRoleLabel(role)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Ekleniyor...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-4 h-4" />
                                    Ekle
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddUserModal;
